import styled, { keyframes } from "styled-components";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { useSeller } from "../../../context/seller";
import { useEffect, useState, ChangeEvent, useMemo } from "react";
import SellerService from "../../../services/seller.service";
import { Reservation, Tour, PaymentMethods, TourInfo } from "../../../types/common.types";
import InputDropdown from "../../common/InputDropdown";
import ToursService from "../../../services/tours.service";
import GeneralInput from "../../common/GeneralInput";
import Calendar from "../../common/CalendarInput";
import { format } from "date-fns";
import removeTimezone from "../../../utils/removeTimezone";
import { FaTrash } from "react-icons/fa";
import Dropdown from "../../common/Dropdown";
import insert from "../../../utils/insert";
import dollar from '../../../assets/common/dollar.svg'
import { Summary } from "../../../types/seller";
import { useGlobal } from "../../../context/global";
import Select from "../../common/Select";
import repartir from "../../../utils/repartir";
import AvisoMoverPrivado from "../../common/AvisoMoverPrivado";

export default function EditReservation() {
    const { setIsLoading } = useGlobal()
    const {setEditReservationId, editReservationId, summary, setSummary, setNewGroupReservations, user} = useSeller()
    const [data, setData] = useState<Reservation>({} as Reservation)
    const [moverTrip, setMoverTrip] = useState(false)
    const [inicial, setInicial] = useState<Reservation>()
    const [otroPrecio, setOtroPrecio] = useState(false)
    const [tours, setTours] = useState<Tour[]>([])
     const [tour, setTour] = useState<TourInfo>()

    useEffect(() => {
        async function fetchTours() {
            try {
                const response = await ToursService.getDetailTourInfo(data?.tour_id || '', user?.user.agency_id || '')
                setTour(response.data)
            } catch (error) {
                console.error('Error fetching tours:', error)
            }
        }
        fetchTours()
    }, [data?.tour_id, user?.user.agency_id])

    const price_options = useMemo(() => {
        if (!tour) return []
        const options = []
        options.push({value: tour.current_price, name: `Current Price: $${tour.current_price}`})
        if (tour.discount_price) options.push({value: tour.discount_price, name: `Discount Price: $${tour.discount_price}`})
        if (tour.depart_price) options.push({value: tour.depart_price, name: `Depart Price: $${tour.depart_price}`})
        // Cuarta opción (16/10/2026): el vendedor escribe el precio que acordó con el pasajero.
        options.push({value: 'otro', name: 'Otro precio'})
        return options
    }, [tour])
    // El precio que ya tiene la reserva queda elegido al abrir (17/10/2026). Antes el select partía en
    // "--" y, si era un precio escrito a mano, no se veía cuál era.
    const precioEnOpciones = price_options.some(o => o.value !== 'otro' && Number(o.value) === Number(data.price))
    const precioElegido = otroPrecio || (!!data.price && !!tour && !precioEnOpciones) ? 'otro' : (data.price ? String(Number(data.price)) : '')
    // Reserva de la venta en curso (se abrió desde el resumen): el pago se registra al final, para toda la venta.
    const enVenta = summary.some(s => s.reservation.id === editReservationId)

    useEffect(() => {
        async function getInfo() {
            setIsLoading(true)
            try {
                const response: Reservation = (await SellerService.getReservation(editReservationId)).data
                // Privado: se edita el vehículo completo (precio y pagos de todos) y el backend lo reparte.
                setData(response.vehiculo ? {...response, price: response.vehiculo.price, payment_methods: response.vehiculo.payment_methods} : response)
                setInicial(response)
            } catch (error) {
                console.log(error)
            }
            try {
                const response = await (await ToursService.list()).data
                setTours(response)
            } catch (error) {
                console.log(error)
            }
            setIsLoading(false)
        }
        getInfo()
    }, [])
    function onSelectPayment(newMethod: string) {
        const new_data = data.payment_methods ? [...data.payment_methods, {name: newMethod}] : [{name: newMethod}]
        setData(prevData => ({...prevData, payment_methods: new_data}))
    }
    function onChangePayment(e: ChangeEvent<HTMLInputElement>) {
        const value = Number(e.target.value)
        const name = e.target.name
        if (!data.payment_methods) {
            const newList = [{name, value}]
            setData(prevData => ({...prevData, payment_methods: newList}))
            return
        }
        const oldItem = data.payment_methods.find((item: PaymentMethods) => item.name === name)
        if (!oldItem) return
        const index = data.payment_methods.indexOf(oldItem)
        console.log(index)
        const filteredList = [...data.payment_methods.filter((item: PaymentMethods) => item.name !== name)]
        const newData = {...oldItem, value}
        setData(prevData => ({...prevData, payment_methods: insert(filteredList, index, newData)}))
    }

    function deletePayment(name: string) {
        const newList = data.payment_methods.filter((item: PaymentMethods) => item.name !== name)
        setData(prevData => ({...prevData, payment_methods: newList}))
    } 

    async function onSubmit() {
        if (data?.reservation_date && data?.tour_id) {
            if (!tours.some(tour => tour.id === data.tour_id)) return
            const newData: Reservation = {
                tour_id: data.tour_id,
                reservation_date: new Date(data.reservation_date),
                price: data.price,
                payment_methods: data.payment_methods,
                cost: data.cost,
                seller_remark: data.seller_remark || '',
                // Las entradas las calcula el backend (16/10/2026): el vendedor solo dice si van incluidas.
                // Si no tocó la opción, no se manda y la reserva queda como estaba.
                ...(data.ticket_included !== inicial?.ticket_included ? { ticket_included: data.ticket_included } : {}),
            } as Reservation
            // En el resumen del comprobante queda la parte de este pasajero, no el vehículo entero.
            if (data.vehiculo) newData.price = repartir(data.price, data.vehiculo.pasajeros)[0]
            try {
                setIsLoading(true)
                await SellerService.editReservation(editReservationId, {...newData, price: data.price, vehiculo: !!data.vehiculo, mover_trip: moverTrip} as any)
                // El monto de entradas lo recalculó el backend: se lee de vuelta para el resumen.
                try {
                    const guardada = (await SellerService.getReservation(editReservationId)).data
                    newData.tickets = Number(guardada.tickets || 0)
                    newData.ticket_included = guardada.ticket_included
                } catch (error) {
                    console.log(error)
                }
                setSummary((prev) => {
                    const oldItem = prev.find(item => item.reservation.id === editReservationId)
                    const filtered = prev.filter(item => item.reservation.id !== editReservationId)
                    return ([...filtered, {...oldItem, reservation: {...oldItem!.reservation, ...newData}}] as Summary[])
                })
                setNewGroupReservations((prev) => {
                    const oldItem = prev.find(item => item.id === editReservationId)
                    const filtered = prev.filter(item => item.id !== editReservationId)
                    return ([...filtered, {...oldItem, ...newData}] as Reservation[])
                })
                setEditReservationId('')
            } catch (error: any) {
                console.log(error)
                if (typeof error?.response?.data === 'string') alert(error.response.data)
            } finally {
                setIsLoading(false)
            }
        }
    }

    return (
        <Main>
            <Modal>
                <div className="close" title="close"
                    onClick={() => setEditReservationId('')}
                >
                    <AiOutlineCloseCircle/>
                </div>
                <h1>Edit reservation</h1>
                {
                    tours.length > 0 &&
                    <InputDropdown
                    options={tours}
                    keys={['id']}
                    value={data.tour_id}
                    selector="id"
                    optionDefaultHeight={35}
                    onChange={(newValue: string) => setData(prevData => ({...prevData, tour_id: newValue}))}
                    />
                }
                {
                    data.vehiculo &&
                    <p style={{margin: '10px 0', padding: '8px 10px', borderRadius: '6px', backgroundColor: '#e0f2fe', color: '#075985', fontSize: '14px'}}>
                        Tour privado de {data.vehiculo.pasajeros} pasajeros: el precio y los pagos son los del vehículo completo y se dividen entre los {data.vehiculo.pasajeros}.
                    </p>
                }
                {
                    data.reservation_date &&
                    <div 
                    style={{margin: '20px 0'}}
                    className="calendar">
                        <Calendar
                        selectedDay={data.reservation_date}
                        setSelectedDay={(newDate: Date) => setData(prevData => ({...prevData, reservation_date: newDate}))}
                        />
                        {typeof data.reservation_date === 'string' ? removeTimezone(data.reservation_date) : format(data.reservation_date, 'dd/MM/yyyy')}
                    </div>
                }
                <AvisoMoverPrivado
                vehiculo={data.vehiculo}
                cambio={data.tour_id !== inicial?.tour_id || data.reservation_date !== inicial?.reservation_date}
                mismoTour={data.tour_id === inicial?.tour_id}
                moverTrip={moverTrip}
                setMoverTrip={setMoverTrip}
                />
                <div className="entradas">
                    <span>Entradas a parques</span>
                    <div className="opciones" role="group" aria-label="Entradas a parques">
                        <button type="button" aria-pressed={data.ticket_included === true} className={data.ticket_included === true ? 'activo' : ''}
                        onClick={() => setData(prev => ({...prev, ticket_included: true}))}>Entradas incluidas</button>
                        <button type="button" aria-pressed={data.ticket_included === false} className={data.ticket_included === false ? 'activo' : ''}
                        onClick={() => setData(prev => ({...prev, ticket_included: false}))}>Pagan en el parque</button>
                    </div>
                    <small>
                        Entradas: ${Number(data.tickets || 0).toLocaleString('es-CL')}. Se calculan solas con las tarifas de cada parque
                        (y se recalculan si cambias el tour o la fecha). Para cambiar el monto, pídeselo a la agencia.
                    </small>
                </div>
                {
                    data.vehiculo &&
                    <p style={{fontSize: '14px'}}>Precio actual del privado: ${data.price}. Elige otro abajo solo si cambia.</p>
                }
                <Select
                style={{margin: '1rem 0'}}
                label={data.vehiculo ? "Precio del privado (vehículo completo)" : "Sale Price Options"}
                options={price_options.map(o => ({...o, value: o.value === 'otro' ? 'otro' : String(Number(o.value))}))}
                value={precioElegido}
                onChange={(e) => {
                    const otro = e.target.value === 'otro'
                    setOtroPrecio(otro)
                    if (!otro) setData({...data, price: Number(e.target.value)})
                }}
                />
                {
                    precioElegido === 'otro' &&
                    <label className="otro-precio" style={{display: "flex", flexDirection: "column", gap: 4, margin: "-6px 0 12px", fontSize: 14}}>
                        {data.vehiculo ? 'Precio del privado (vehículo completo)' : 'Precio por pasajero'}
                        <input type="number" min={1} placeholder="Ej: 35000" aria-label="Otro precio"
                        value={data.price || ''}
                        onChange={e => setData({...data, price: Number(e.target.value)})} />
                    </label>
                }
                {
                    enVenta ? <p style={{fontSize: 13, color: '#555', margin: '8px 0 16px'}}>El pago se registra al final de la venta.</p> : <>
                <h3 className="payment-title">{data.vehiculo ? 'Pagos del vehículo (total)' : 'Payment methods'}</h3>
                {
                    data?.payment_methods?.map((method, idx: number) => {
                        return (
                            <div className="payment_method" key={idx}>
                                <button
                                onClick={() => deletePayment(method.name)}
                                >
                                    <FaTrash/>
                                </button>
                                <GeneralInput
                                value={method.value || ''}
                                name={method.name}
                                type="number"
                                placeholder="Amount"
                                label={method.name}
                                image={dollar}
                                onChange={onChangePayment}
                                />
                            </div>
                        )
                    })
                }
                <Dropdown
                style={{marginTop: '20px'}}
                options={['Efectivo', 'Tarjeta Santander', 'Tarjeta BCI', 'Trasnferencia Santander', 'Transferencia BCI', 'Wise David', 'Link de Pago', 'Deuda'].filter(str => !data?.payment_methods?.map(item => item.name).includes(str))}
                title="Payment methods"
                onSelect={onSelectPayment}
                />
                </>
                }
                <GeneralInput
                label="Cost"
                type="number"
                value={data.cost || ''}
                onChange={e => setData(prevData => ({...prevData, cost: Number(e.target.value)}))}
                />
                <GeneralInput
                style={{marginTop: '20px'}}
                label="Remark for this tour (optional)"
                value={data.seller_remark || ''}
                onChange={e => setData(prevData => ({...prevData, seller_remark: e.target.value}))}
                />
                <button 
                type="submit"
                onClick={onSubmit}
                >Submit</button>
            </Modal>
        </Main>
    )
}

const Main = styled.div`
z-index: 100;
position: fixed;
top: 0;
left: 0;
width: 100vw;
height: 100vh;
background-color: ${props => props.theme.blackTransparent};
`
const showUpModal = keyframes`
    from {
        top: 100%;
    }
    to {
        top: 50%;
    }
`

const Modal = styled.div`
.entradas {
    margin: 14px 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    > span { font-weight: 600; }
    .opciones { display: flex; flex-wrap: wrap; gap: 6px; }
    .opciones button { border: 1px solid #c9c9c9; border-radius: 999px; padding: 5px 12px; background: #fff; cursor: pointer; font-size: 13px; }
    .opciones button.activo { background: #2c3e50; border-color: #2c3e50; color: #fff; }
    small { color: #777; font-size: 12px; }
}
width: 80%;
max-height: 75%;
overflow: hidden;
overflow-y: unset;
max-width: 500px;
position: absolute;
top: 50%;
left: 50%;
transform: translate(-50%, -50%);
background-color: ${props => props.theme.white};
box-shadow: 0 0 20px ${props => props.theme.blackTransparent};
padding: 20px;
animation: ${showUpModal} .3s ease forwards;
.close {
    position: absolute;
    right: 20px;
    top: 20px;
    font-size: 22px;
    cursor: pointer;
}
h1 {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 20px;
}
.payment_method {
    margin-bottom: 10px;
    position: relative;
    button {
        position: absolute;
        right: 0;
        top: 0;
        height: 25px;
        width: 25px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        background-color: rgba(0,0,0,0);
        border-radius: 50%;
        cursor: pointer;
        border: 1px solid ${props => props.theme.dark};
    }
    h4 {
        font-weight: 400;
        margin-bottom: 5px;
    }
    label {
        text-transform: capitalize;
    }
}
> button.back {
    background-color: rgba(0,0,0,0);
    border: none;
    font-size: 20px;
    cursor: pointer;
    transition: transform .3s ease;
    &:hover {
        transform: translateY(-3px);
    }
}
h3.payment-title {
    font-weight: 500;
    margin-bottom: 10px;
}
> .calendar {
    display: flex;
    gap: 10px;
    align-items: center;
}
> button {
    margin-top: 20px;
    display: inline-block;
    padding: 0 10px;
    height: 2.6em;
    line-height: 2.5em;
    overflow: hidden;
    font-size: 17px;
    z-index: 1;
    background-color: ${props => props.theme.white};
    color: ${props => props.theme.blue};
    border: 2px solid ${props => props.theme.blue};
    border-radius: 6px;
    position: relative;
    cursor: pointer;
}

> button::before {
    position: absolute;
    content: "";
    background: ${props => props.theme.blue};
    width: calc(100% + 100px);
    height: 200px;
    z-index: -1;
    border-radius: 50%;
}

> button:hover {
    color: ${props => props.theme.white};
}

> button:before {
    top: 100%;
    left: 100%;
    transition: .3s all;
}

> button:hover::before {
    top: -30px;
    left: -30%;
}
`